import React, { Component } from 'react';
import Inherit from '../../core/inherit';
import withNavigate from '../../core/withNavigate';
import { Layout, TableSkeleton, PageHeader, StatCard, SectionCard, FilterBar, FilterField, EmptyState, StatusTag } from '../common';
import { Button, DatePicker, Select, Table, Tooltip } from 'antd';
import { Route, MapPin, Clock, Car, TriangleAlert, RotateCw, UserRound, Calendar, Info } from 'lucide-react';
import dayjs from 'dayjs';
import RouteEfficiencyController, { EMPTY_ROUTE_FILTERS } from './routeEfficiency.controller';
import RouteEfficiencyMap from './elements/RouteEfficiencyMap';
import { getGpsAccuracy } from './elements/gps';

const Dash = () => <span className="text-surface-300">—</span>;

class RouteEfficiency extends Inherit(RouteEfficiencyController, Component) {
    state = {
        loading: true,
        routeLoading: false,
        promotors: [],
        selectedPromotor: null,
        selectedDate: dayjs(),
        routeData: null,
        selectedVisitIndex: null,
        filters: EMPTY_ROUTE_FILTERS,
        error: false
    };

    get columns() {
        return [
            {
                title: '#',
                key: 'index',
                width: 48,
                render: (_, __, index) => <span className="font-mono text-xs text-surface-400 tabular-nums">{index + 1}</span>
            },
            {
                title: 'Piso de Venta',
                dataIndex: ['store', 'name'],
                key: 'store',
                render: (v) => (v ? <span className="font-medium text-surface-900">{v}</span> : <Dash />)
            },
            { title: 'Cliente', dataIndex: 'clientName', key: 'clientName', render: (v) => v || <Dash /> },
            {
                title: 'Canal',
                dataIndex: 'channel',
                key: 'channel',
                width: 110,
                render: (v) => (v ? <StatusTag>{v}</StatusTag> : <Dash />)
            },
            {
                title: 'Región',
                dataIndex: 'regions',
                key: 'regions',
                render: (regions) =>
                    Array.isArray(regions) && regions.length ? (
                        <div className="flex flex-wrap gap-1">
                            {regions.map((r) => (
                                <StatusTag key={r} tone="info">
                                    {r}
                                </StatusTag>
                            ))}
                        </div>
                    ) : (
                        <Dash />
                    )
            },
            {
                title: 'Inicio',
                dataIndex: 'startTime',
                key: 'startTime',
                width: 80,
                align: 'right',
                render: (v) => (v ? <span className="tabular-nums">{dayjs(v).format('HH:mm')}</span> : <Dash />)
            },
            {
                title: 'Fin',
                dataIndex: 'endTime',
                key: 'endTime',
                width: 80,
                align: 'right',
                render: (v) => (v ? <span className="tabular-nums">{dayjs(v).format('HH:mm')}</span> : <Dash />)
            },
            {
                title: 'Duración',
                dataIndex: 'durationMinutes',
                key: 'durationMinutes',
                width: 96,
                align: 'right',
                render: (v) => (v != null ? <span className="tabular-nums">{`${v} min`}</span> : <Dash />)
            },
            {
                title: 'Tránsito',
                dataIndex: 'transitMinutes',
                key: 'transitMinutes',
                width: 96,
                align: 'right',
                render: (v) => (v != null ? <span className="tabular-nums">{`${v} min`}</span> : <Dash />)
            },
            {
                title: 'Coord. PDV',
                key: 'storeCoords',
                width: 160,
                render: (_, record) => {
                    const coords = record.theoreticalCoordinates || record.store?.coordinates;
                    if (coords?.latitude == null) return <Dash />;
                    return (
                        <Tooltip title="Ubicación registrada del piso de venta">
                            <span className="text-xs font-mono tabular-nums text-violet-fg">
                                {coords.latitude.toFixed(5)}, {coords.longitude.toFixed(5)}
                            </span>
                        </Tooltip>
                    );
                }
            },
            {
                title: 'Coord. storecheck',
                key: 'auditCoords',
                width: 160,
                render: (_, record) => {
                    const coords = record.auditCoordinates;
                    if (coords?.latitude == null) return <Dash />;
                    return (
                        <Tooltip title="GPS capturado durante la auditoría">
                            <span className="text-xs font-mono tabular-nums text-success-fg">
                                {coords.latitude.toFixed(5)}, {coords.longitude.toFixed(5)}
                            </span>
                        </Tooltip>
                    );
                }
            },
            {
                title: 'Precisión GPS',
                dataIndex: 'distanceMeters',
                key: 'distanceMeters',
                width: 130,
                align: 'right',
                render: (v) => {
                    const gps = getGpsAccuracy(v);
                    return (
                        <Tooltip title={gps.description}>
                            <span>
                                <StatusTag tone={gps.tone} dot className="tabular-nums">
                                    {gps.label}
                                </StatusTag>
                            </span>
                        </Tooltip>
                    );
                }
            }
        ];
    }

    renderSummaryCards(visits) {
        const summary = this.summarizeVisits(visits);
        const hasAlerts = summary.gpsIssues > 0;

        return (
            <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 mb-4">
                <StatCard label="Visitas" value={summary.visits} icon={<MapPin />} tone="brand" hint="Storechecks del día" />
                <StatCard
                    label="Duración prom."
                    value={`${summary.avgDuration} min`}
                    icon={<Clock />}
                    tone="info"
                    hint={`Sobre ${summary.withDuration} visita${summary.withDuration !== 1 ? 's' : ''} con duración registrada`}
                    help="Promedio entre inicio y fin del storecheck. Las visitas sin hora de fin no cuentan."
                />
                <StatCard
                    label="Tránsito prom."
                    value={`${summary.avgTransit} min`}
                    icon={<Car />}
                    tone="violet"
                    hint={`Sobre ${summary.withTransit} traslado${summary.withTransit !== 1 ? 's' : ''} medido${summary.withTransit !== 1 ? 's' : ''}`}
                    help="Tiempo entre el fin de una visita y el inicio de la siguiente."
                />
                <StatCard
                    label="Alertas GPS"
                    value={summary.gpsIssues}
                    valueTone={hasAlerts ? 'danger' : undefined}
                    icon={<TriangleAlert />}
                    tone={hasAlerts ? 'danger' : 'success'}
                    hint="Storechecks a más de 500 m del PDV"
                    help="Distancia entre el GPS capturado en el storecheck y la ubicación registrada del piso de venta."
                />
            </div>
        );
    }

    renderFilters(visits) {
        const { promotors, selectedPromotor, selectedDate, filters } = this.state;
        const filterOptions = this.visitFilterOptions(visits);

        return (
            <FilterBar>
                <FilterField label="Promotor" width={288}>
                    <Select
                        showSearch
                        allowClear
                        placeholder="Seleccionar promotor"
                        optionFilterProp="label"
                        value={selectedPromotor}
                        options={promotors.map((p) => ({ value: p._id, label: p.name }))}
                        onChange={(id) => this.selectPromotor(id)}
                    />
                </FilterField>
                <FilterField label="Fecha" width={170}>
                    <DatePicker value={selectedDate} onChange={(d) => this.selectDate(d)} format="DD/MM/YYYY" />
                </FilterField>
                {visits.length > 0 && (
                    <>
                        <FilterField label="Canal">
                            <Select
                                mode="multiple"
                                allowClear
                                placeholder="Canal"
                                maxTagCount="responsive"
                                optionFilterProp="label"
                                value={filters.channel}
                                options={filterOptions.channel}
                                onChange={(v) => this.setFilter({ channel: v })}
                            />
                        </FilterField>
                        <FilterField label="Región">
                            <Select
                                mode="multiple"
                                allowClear
                                placeholder="Región"
                                maxTagCount="responsive"
                                optionFilterProp="label"
                                value={filters.region}
                                options={filterOptions.region}
                                onChange={(v) => this.setFilter({ region: v })}
                            />
                        </FilterField>
                        <FilterField label="Cliente" width={220}>
                            <Select
                                mode="multiple"
                                allowClear
                                showSearch
                                placeholder="Cliente"
                                maxTagCount="responsive"
                                optionFilterProp="label"
                                value={filters.client}
                                options={filterOptions.client}
                                onChange={(v) => this.setFilter({ client: v })}
                            />
                        </FilterField>
                    </>
                )}
            </FilterBar>
        );
    }

    renderRoute(visits, filteredVisits) {
        const { routeData, selectedDate, selectedVisitIndex, filters } = this.state;
        const filtersActive = filters.channel.length || filters.region.length || filters.client.length;
        const plural = visits.length !== 1 ? 's' : '';

        return (
            <>
                {routeData?.promotor && (
                    <div className="shelf-card mb-4 flex flex-wrap items-center gap-3 px-4 py-3">
                        <span className="flex h-9 w-9 items-center justify-center rounded-full bg-primary-50 text-primary-600">
                            <UserRound className="text-[15px]" />
                        </span>
                        <div className="min-w-0 flex-1">
                            <div className="text-sm font-semibold text-surface-900 truncate">{routeData.promotor.name}</div>
                            {routeData.promotor.email && <div className="text-xs text-surface-500 truncate">{routeData.promotor.email}</div>}
                        </div>
                        <StatusTag icon={<Calendar />}>{dayjs(selectedDate).format('DD/MM/YYYY')}</StatusTag>
                    </div>
                )}

                {this.renderSummaryCards(filteredVisits)}

                {filteredVisits.length > 0 && (
                    <div className="mb-4">
                        <RouteEfficiencyMap
                            visits={filteredVisits}
                            selectedIndex={selectedVisitIndex}
                            onSelectVisit={(index) => this.selectVisit(index)}
                        />
                    </div>
                )}

                <SectionCard
                    flush
                    icon={<Route />}
                    title={filtersActive ? `${filteredVisits.length} de ${visits.length} visita${plural}` : `${visits.length} visita${plural}`}
                    description="Haz clic en una fila para ubicarla en el mapa">
                    {visits.length === 0 ? (
                        <EmptyState icon={<MapPin />} title="Sin visitas" description="Sin visitas registradas en esta fecha" />
                    ) : filteredVisits.length === 0 ? (
                        <EmptyState icon={<MapPin />} title="Sin resultados" description="Sin visitas para los filtros seleccionados" />
                    ) : (
                        <Table
                            dataSource={filteredVisits}
                            columns={this.columns}
                            rowKey={(r) => r.storeCheckId}
                            pagination={false}
                            size="middle"
                            scroll={{ x: 'max-content' }}
                            onRow={(record, index) => ({
                                onClick: () => this.selectVisit(index),
                                className: selectedVisitIndex === index ? '!bg-primary-50 cursor-pointer' : 'cursor-pointer'
                            })}
                        />
                    )}
                    <div className="flex flex-col gap-2 border-t border-surface-100 px-5 py-3">
                        <div className="flex flex-wrap items-center gap-2">
                            <span className="text-xs font-medium text-surface-500">Leyenda GPS:</span>
                            <StatusTag tone="success" dot>
                                ≤ 100 m — Preciso
                            </StatusTag>
                            <StatusTag tone="warning" dot>
                                101–500 m — Moderado
                            </StatusTag>
                            <StatusTag tone="danger" dot>
                                &gt; 500 m — Desviado
                            </StatusTag>
                            <StatusTag tone="neutral" dot>
                                Sin GPS
                            </StatusTag>
                        </div>
                        <p className="m-0 flex items-start gap-1.5 text-xs text-surface-500">
                            <Info className="mt-px shrink-0" />
                            <span>
                                Una celda con “—” significa que el dato no se capturó en el storecheck (por ejemplo, sin hora de fin o sin
                                GPS). Esas visitas no cuentan en los promedios.
                            </span>
                        </p>
                    </div>
                </SectionCard>
            </>
        );
    }

    render() {
        const { loading, routeLoading, selectedPromotor, routeData, error } = this.state;
        const visits = routeData?.visits || [];
        const filteredVisits = this.applyVisitFilters(visits);

        return (
            <Layout current="route-efficiency">
                <PageHeader
                    icon={<Route />}
                    title="Eficiencia de Ruta"
                    description="Visitas diarias, precisión GPS vs ubicación teórica y tiempos de tránsito entre PDVs."
                />

                {error ? (
                    <div className="shelf-card">
                        <EmptyState
                            tone="danger"
                            icon={<TriangleAlert />}
                            title="Error al cargar promotores"
                            description="No pudimos obtener la lista de promotores."
                            action={
                                <Button icon={<RotateCw />} onClick={() => this.retry()}>
                                    Reintentar
                                </Button>
                            }
                        />
                    </div>
                ) : loading ? (
                    <TableSkeleton rows={6} columns={7} />
                ) : (
                    <div className="animate-in">
                        {this.renderFilters(visits)}

                        {!selectedPromotor ? (
                            <div className="shelf-card">
                                <EmptyState
                                    icon={<UserRound />}
                                    title="Elige un promotor"
                                    description="Selecciona un promotor para ver su ruta del día"
                                />
                            </div>
                        ) : routeLoading ? (
                            <TableSkeleton rows={8} columns={7} />
                        ) : (
                            this.renderRoute(visits, filteredVisits)
                        )}
                    </div>
                )}
            </Layout>
        );
    }
}

export default withNavigate(RouteEfficiency);
